import { ArrowLeftOutlined, DatabaseOutlined } from '@ant-design/icons'
import { Alert, Button, Progress, Segmented, Skeleton, Tooltip } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { getStorageTrend, getTenantOverview } from '../api'
import { useUiLocale } from '../../i18n/LocaleProvider'
import type { StorageSummary, StorageTrendPoint } from '../types'
import { formatBytes } from '../ui/bytesFormat'
import TrendLineChart from './overview/TrendLineChart'
import './overview/overview.css'

const TREND_DAYS_OPTIONS = [7, 30, 90] as const

function formatShortDate(iso: string, locale: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat(locale, { month: 'numeric', day: 'numeric' }).format(d)
}

export default function StorageDetailPage() {
  const { t } = useTranslation(['overview', 'actions'])
  const { locale } = useUiLocale()
  const { workspaceId = '' } = useParams()
  const navigate = useNavigate()

  const categoryMeta = useMemo(
    () => [
      {
        key: 'image_bytes' as const,
        label: t('overview:assetCategory.image'),
        color: '#5b5ff5',
        route: (id: string) => `/workspace/${id}/images`,
        routeLabel: t('overview:storageDetail.breakdown.goToImages'),
      },
      {
        key: 'video_bytes' as const,
        label: t('overview:assetCategory.video'),
        color: '#0ea5e9',
        route: (id: string) => `/workspace/${id}/video-generation`,
        routeLabel: t('overview:storageDetail.breakdown.goToVideo'),
      },
      {
        key: 'document_bytes' as const,
        label: t('overview:assetCategory.document'),
        color: '#f59e0b',
        route: null,
        routeLabel: null,
      },
      {
        key: 'other_bytes' as const,
        label: t('overview:assetCategory.other'),
        color: '#94a3b8',
        route: null,
        routeLabel: null,
      },
    ],
    [t],
  )

  const [storage, setStorage] = useState<StorageSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [trendDays, setTrendDays] = useState<7 | 30 | 90>(30)
  const [trendPoints, setTrendPoints] = useState<StorageTrendPoint[]>([])
  const [trendLoading, setTrendLoading] = useState(true)

  useEffect(() => {
    if (!workspaceId) return
    setLoading(true)
    getTenantOverview(workspaceId)
      .then((r) => setStorage(r.data.storage))
      .catch((e) => setError((e as Error).message || t('overview:storageDetail.loadStorageFailed')))
      .finally(() => setLoading(false))
  }, [workspaceId, t])

  useEffect(() => {
    if (!workspaceId) return
    setTrendLoading(true)
    getStorageTrend(workspaceId, trendDays)
      .then((r) => setTrendPoints(r.data.points))
      .catch(() => setTrendPoints([]))
      .finally(() => setTrendLoading(false))
  }, [workspaceId, trendDays])

  return (
    <div className="tc-ov-page" data-testid="storage-detail-page">
      <div className="tc-page-header">
        <div>
          <h1 className="tc-page-title">{t('overview:storageDetail.title')}</h1>
          <p className="tc-page-desc">{t('overview:storageDetail.subtitle')}</p>
        </div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/workspace/${workspaceId}/overview`)}>
          {t('overview:storageDetail.backToOverview')}
        </Button>
      </div>

      {loading && <Skeleton active paragraph={{ rows: 4 }} />}
      {error && !loading && <Alert type="error" showIcon message={t('overview:storageDetail.loadFailed')} description={error} />}

      {storage && (
        <>
          <div className="tc-data-card tc-ov-panel">
            <div className="tc-ov-panel-head">
              <span className="tc-ov-panel-title">
                <DatabaseOutlined /> {t('overview:storageDetail.usage.title')}
              </span>
              <Tooltip title={storage.accounting_method}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', cursor: 'help' }}>
                  {t('overview:storageDetail.usage.accountingMethod')}
                </span>
              </Tooltip>
            </div>
            <div className="tc-ov-card-value" style={{ fontSize: 26 }}>
              {formatBytes(storage.used_bytes)} <span className="tc-ov-card-value-unit">/ {formatBytes(storage.quota_bytes)}</span>
            </div>
            <Progress
              percent={storage.usage_percent}
              strokeColor={
                storage.status === 'critical' ? 'var(--error-text)' : storage.status === 'warning' ? 'var(--warning-text)' : 'var(--primary)'
              }
              style={{ marginTop: 12, maxWidth: 480 }}
            />
            <div className="tc-ov-trend-footer" style={{ borderTop: 'none', paddingTop: 12 }}>
              <div>
                {t('overview:storageDetail.usage.usageRate')}
                <span className="tc-ov-trend-footer-value">{storage.usage_percent}%</span>
              </div>
              <div>
                {t('overview:storageDetail.usage.used')}
                <span className="tc-ov-trend-footer-value">{formatBytes(storage.used_bytes)}</span>
              </div>
              <div>
                {t('overview:storageDetail.usage.available')}
                <span className="tc-ov-trend-footer-value">{formatBytes(storage.available_bytes)}</span>
              </div>
              <div>
                {t('overview:storageDetail.usage.status')}
                <span className="tc-ov-trend-footer-value">{storage.status_label}</span>
              </div>
            </div>
          </div>

          <div className="tc-data-card tc-ov-panel">
            <div className="tc-ov-panel-head">
              <span className="tc-ov-panel-title">{t('overview:storageDetail.breakdown.title')}</span>
            </div>
            {categoryMeta.map((cat) => {
              const bytes = storage[cat.key]
              const percent = storage.used_bytes > 0 ? Math.round((bytes / storage.used_bytes) * 1000) / 10 : 0
              return (
                <div className="tc-ov-asset-row" key={cat.key}>
                  <div className="tc-ov-asset-row-head">
                    <span className="tc-ov-asset-dot" style={{ background: cat.color }} />
                    <span>{cat.label}</span>
                    <span className="tc-ov-asset-row-values">{formatBytes(bytes)}</span>
                    <span className="tc-ov-asset-row-percent">{percent}%</span>
                    {cat.route && (
                      <Button type="link" size="small" onClick={() => navigate(cat.route!(workspaceId))}>
                        {cat.routeLabel}
                      </Button>
                    )}
                  </div>
                  <Progress percent={percent} size="small" showInfo={false} strokeColor={cat.color} />
                </div>
              )
            })}
          </div>

          <div className="tc-data-card tc-ov-panel">
            <div className="tc-ov-panel-head">
              <span className="tc-ov-panel-title">{t('overview:panels.storageTrend.title')}</span>
              <Segmented
                size="small"
                value={trendDays}
                onChange={(v) => setTrendDays(v as 7 | 30 | 90)}
                options={TREND_DAYS_OPTIONS.map((d) => ({
                  label: t('overview:panels.storageTrend.lastDays', { days: d }),
                  value: d,
                }))}
              />
            </div>
            {trendLoading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : (
              <TrendLineChart
                points={trendPoints.map((p) => ({ date: p.date, value: p.used_bytes }))}
                formatValue={(v) => formatBytes(v)}
                formatDate={(d) => formatShortDate(d, locale)}
                height={260}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}
